import React, { useEffect, useRef, useState } from 'react';
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from "next-i18next";
import { useRouter } from 'next/router';
import Link from 'next/link';
import Mail from './images/mail';
import LinkedIn from './images/linkedin';
import CloverLogo from './images/cloverLogo';
import Global from './images/global';
import styles from '../styles/Footer.module.css';
import Image from 'next/image';
import ProductsIcon from './images/productsIcon';
import Iyzico from './images/iyzico';
import Mastercard from './images/mastercard';
import Visa from './images/visa';

interface FooterProps {
  logo?: "original" | "green";
  theme?: string;
  color?: string;
}

const Footer = (_props: InferGetStaticPropsType<typeof getStaticProps> & FooterProps) => {
  const { logo, theme, color } = _props;

  const { t } = useTranslation('common');
  const router = useRouter();
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const logoType = logo || 'original';
  const isLanguage = router.locale?.toUpperCase().toString();

  // Email obfuscation to prevent Cloudflare from injecting email-decode.min.js
  const emailAddress = React.useMemo(() => {
    return ['info', '@', 'stoneity', '.', 'com'].join('');
  }, []);

  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(e.target)) {
        setIsLanguageMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handler)

    return () => {
      document.removeEventListener('mousedown', handler)
    }
  }, []);

  return (
    <footer className={styles.footer}>
      <div className={styles.footerFlex}>
        <div className={styles.footerContent}>
          <Image
            className={styles.footerLogo}
            src={logoType === "original" ? "/stoneityLogo.png" : logoType === "green" ? "/stoneityLogoGreen.png" : ""}
            alt=""
            width={130}
            height={32}
            sizes="130px"
          />
          <div className={styles.footerTop}>
            <div className={styles.footerTopLeft}>
              {/* <Link className={styles.footerLink} locale={router.locale} href={"/"} style={{ fontWeight: "600" }}>{t('homepage')}</Link> */}
              <div className={styles.footerTopPagesLinkWrapper}>
                <div className={styles.footerTopPagesLinkColumn}>
                  <div className={styles.title}>{t("company")}</div>
                  <Link className={styles.text} href={'/privacy-policy'}>{t("confidentialityPersonelTitle")}</Link>
                  <Link className={styles.text} href={'/distance-sales-agreement'}>{t("distanceSalesAgreement")}</Link>
                  <Link className={styles.text} href={'/delivery-refund-terms'}>{t("deliveryAndReturnTerms")}</Link>
                </div>
                <div className={styles.footerTopPagesLinkLine}></div>
                <div className={styles.footerTopPagesLinkColumn}>
                  <div className={styles.title}>{t("products")}</div>
                  <Link className={styles.productsLogo} href={'/work-management/documents'}>
                    <ProductsIcon width='20px' height='20px' color="#FC584A" />
                    Work Management
                  </Link>
                  <Link className={styles.productsLogo} href={'/service-management/documents'}>
                    <ProductsIcon width='20px' height='20px' color="#478B5A" />
                    Service Management
                  </Link>
                  <Link className={`${styles.text} ${styles.textHidden}`} href={'demo-pages?type=portal'}>Portal Demo</Link>
                  <Link className={`${styles.text} ${styles.textHidden}`} href={'demo-pages?type=agent'}>Agent Demo</Link>
                  <Link className={styles.productsLogo} href={'/bmc-helix-itsm'}>
                    <img src="/bmc-helix-logo.svg" alt="" />
                    <span style={{ paddingTop: "2px" }}>ITSM</span>
                  </Link>
                  <Link className={styles.productsLogo} href={'/bmc-helix-discovery'}>
                    <img src="/bmc-helix-logo.svg" alt="" />
                    <span style={{ paddingTop: "2px" }}>Discovery</span>
                  </Link>
                  <Link className={styles.productsLogo} href={'/bmc-control-m'}>
                    <img src="/bmc-logo.svg" alt="" />
                    <span style={{ paddingTop: "3px" }}>Control-M</span>
                  </Link>
                </div>
                <div className={styles.footerTopPagesLinkLine}></div>
                <div className={styles.footerTopPagesLinkColumn}>
                  <div className={styles.title}>{t("solutions")}</div>
                  <div className={styles.footerTopPagesLinkFlex}>
                    <div className={styles.footerTopPagesLink}>
                      <Link className={styles.text} href={'/solutions/itsm-incident-management'}>{t("incidentManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/itsm-problem-management'}>{t("problemManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/itsm-request-management'}>{t("requestManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/itsm-change-management'}>{t("changeManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/itsm-self-service-portal'}>{t("selfServicePortal")}</Link>
                      <Link className={styles.text} href={'/solutions/itsm-service-automation'}>{t("serviceAutomation")}</Link>
                      <Link className={styles.text} href={'/solutions/csm-customer-service-management'}>{t("customerServiceManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/csm-sales-and-order-management'}>{t("salesAndOrderManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/ee-hr-service-delivery'}>{t("hrServiceDelivery")}</Link>
                      <Link className={styles.text} href={'/solutions/ee-workspace-service-delivery'}>{t("workspaceServiceDelivery")}</Link>
                      <Link className={styles.text} href={'/solutions/ee-enterprise-service-management'}>{t("esmEnterpriseServiceManagement")}</Link>
                    </div>
                    <div className={styles.footerTopPagesLink}>
                      <Link className={styles.text} href={'/solutions/ee-field-service-management'}>{t("fieldServiceManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/pasm-project-portfolio'}>{t("projectPortfolio")}</Link>
                      <Link className={styles.text} href={'/solutions/pasm-project-planning'}>{t("projectPlanning")}</Link>
                      <Link className={styles.text} href={'/solutions/pasm-okr'}>{t("okr")}</Link>
                      <Link className={styles.text} href={'/solutions/pasm-capacity-planning'}>{t("capacityPlanning")}</Link>
                      <Link className={styles.text} href={'/solutions/pasm-project-tracking'}>{t("projectTracking")}</Link>
                      <Link className={styles.text} href={'/solutions/pasm-strategic-initiative'}>{t("strategicInitiative")}</Link>
                      <Link className={styles.text} href={'/solutions/wm-workflow-management'}>{t("workflowManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/wm-task-management'}>{t("taskManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/wm-contract-management'}>{t("contractManagement")}</Link>
                      <Link className={styles.text} href={'/solutions/wm-account-payable-operations'}>{t("accountsPayableOperations")}</Link>
                    </div>
                  </div>
                </div>
                <div className={styles.footerTopPagesLinkLine}></div>
                <div className={styles.footerTopPagesLinkColumn}>
                  <div className={styles.title}>{t("documents")}</div>
                  <Link className={styles.productsLogo} href={'/work-management/documents'}>
                    <ProductsIcon width='20px' height='20px' color="#FC584A" />
                    Work Management
                  </Link>
                  <Link className={styles.productsLogo} href={'/service-management/documents'}>
                    <ProductsIcon width='20px' height='20px' color="#478B5A" />
                    Service Management
                  </Link>
                </div>
              </div>
            </div>
            <div className={styles.footerTopRight}>
              <Mastercard />
              <Visa />
              <Iyzico />
            </div>
          </div>
          <div className={styles.line}></div>
          <div className={styles.footerBottom}>
            <div className={styles.footerBottomLeft}>
              <div className={styles.languageWrapper} ref={languageMenuRef}>
                <div className={styles.languageButton} onClick={() => { setIsLanguageMenuOpen(!isLanguageMenuOpen) }}>
                  <Global color={color ? color : "#03555e"} />
                  {router.locale?.toUpperCase()}
                </div>
                <div className={`${styles.languageButtonMenu} ${isLanguageMenuOpen ? styles.languageButtonMenuActive : ''}`}>
                  <div className={styles.languageButtonMenuContent}>
                    <Link href={router.pathname} locale={"en"} onClick={() => { setIsLanguageMenuOpen(false) }}>EN</Link>
                    <Link href={router.pathname} locale={"tr"} onClick={() => { setIsLanguageMenuOpen(false) }}>TR</Link>
                  </div>
                </div>
              </div>
              <Link className={styles.socialIcon} href={`mailto:${emailAddress}`} aria-label={t("emailLinkLabel")}>
                <Mail color={color ? color : "#03555e"} aria-hidden="true" />
              </Link>
              <Link className={styles.socialIcon} href={"https://linkedin.com/company/stoneity"} target='_blank' aria-label={t("linkedinLinkLabel")}>
                <LinkedIn color={color ? color : "#03555e"} aria-hidden="true" />
              </Link>
            </div>
            <div className={styles.footerBottomRight}>
              {isLanguage === "TR" ?
                <>
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.stoneity.mobile"
                    className={styles.footerbottomRightIconLink}
                    aria-label="Google Play'den indir"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/playStoreBadgeTr.svg" alt="" aria-hidden="true" />
                  </Link>
                  <Link
                    href={'https://apps.apple.com/tr/app/stoneity/id6757382888'}
                    className={styles.footerbottomRightIconLink}
                    aria-label="App Store'dan indir"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/appStoreBadgeTr.svg" alt="" aria-hidden="true" />
                  </Link>
                </>
                :
                <>
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.stoneity.mobile"
                    className={styles.footerbottomRightIconLink}
                    aria-label="Download on Google Play"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/playStoreBadgeEn.svg" alt="" aria-hidden="true" />
                  </Link>
                  <Link
                    href={'https://apps.apple.com/tr/app/stoneity/id6757382888'}
                    className={styles.footerbottomRightIconLink}
                    aria-label="Download on the App Store"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/appStoreBadgeEn.svg" alt="" aria-hidden="true" />
                  </Link>
                </>
              }
            </div>
          </div>
          <div className={styles.address}><span>{t("address")}:</span> {t("companyAddress")}</div>
          <div className={styles.copyright}>{t('copyright')} © {new Date().getFullYear()} {t('allRightsReserved')}.</div>
        </div>
        <div className={styles.cloverLogo}>
          <CloverLogo />
        </div>
      </div>
    </footer>
  )
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: { ...(await serverSideTranslations(locale || 'en', ['common'])) },
});

export default Footer;
